import { canStartDrag } from "@common/hocs/dnd";
import { EMPTY_ARRAY, removeItem, toMap } from "@common/lib/array";
import {
  type Activators,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type DropAnimation,
  KeyboardSensor,
  MeasuringStrategy,
  type Modifier,
  PointerSensor,
  type PointerSensorOptions,
  type SensorOptions,
  type UniqueIdentifier,
  closestCenter,
  defaultDropAnimation,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { without } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SortableTreeItem } from "./SortableTreeItem";
import { sortableTreeKeyboardCoordinates } from "./lib";
import type { FlattenedItem, SensorContext, TreeItem, TreeItemComponentType, TreeItems } from "./types";
import { buildTree, findItemDeep, flattenTree, getProjection, removeChildrenOf } from "./utilities";

const patchActivator = <T extends SensorOptions>(activators: Activators<T>, eventName: string): Activators<T> => {
  const originalActivatorIndex = activators.findIndex((x) => x.eventName === eventName);
  const originalActivator = activators[originalActivatorIndex];

  activators[originalActivatorIndex] = {
    eventName: originalActivator.eventName,
    handler(...args) {
      return canStartDrag() && originalActivator.handler(...args);
    }
  };
  return activators;
};

patchActivator(KeyboardSensor.activators, "onKeyDown");
patchActivator(PointerSensor.activators, "onPointerDown");

const defaultPointerSensorOptions: PointerSensorOptions = {
  activationConstraint: {
    distance: 3
  }
};

const dropAnimationDefaultConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5
        })
      }
    ];
  },
  easing: "ease-out",
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing
    });
  }
};

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always
  }
};

const adjustTranslate: Modifier = ({ transform }) => {
  return {
    ...transform,
    y: transform.y - 25
  };
};
const MODIFIERS = [adjustTranslate];

export type OnDropArgs<T, TID = UniqueIdentifier> = {
  source: T;
  parentId: TID | null;
  index: number;
  items: T[];
};

export type SortableTreeExpanded = boolean | UniqueIdentifier;
export type SortableTreeItemData = Record<string, any> & {
  parentTypes?: string[] | null;
  type?: string;
};
type SortableTreePropsBase<TData extends SortableTreeItemData, TElement extends HTMLElement> = {
  activeId?: UniqueIdentifier | null;
  dndContextProps?: React.ComponentProps<typeof DndContext>;
  dropAnimation?: DropAnimation | null;
  expandedIds: UniqueIdentifier[];
  indentationWidth?: number;
  indicator?: boolean;
  isNoAnimation?: boolean;
  isNoKeyboard?: boolean;
  isSameDepthDrag?: boolean;
  items: TreeItems<TData>;
  onDrop?: (args: OnDropArgs<TreeItem<TData>>) => void;
  toggleExpanded: (id: UniqueIdentifier) => void;
  TreeItemComponent: TreeItemComponentType<TData, TElement>;
};
const SortableTreeBase = <TreeItemData extends SortableTreeItemData, TElement extends HTMLElement = HTMLDivElement>({
  activeId: active,
  expandedIds,
  dndContextProps,
  dropAnimation,
  indentationWidth = 20,
  indicator = false,
  isNoAnimation,
  isNoKeyboard,
  isSameDepthDrag,
  items,
  onDrop,
  toggleExpanded,
  TreeItemComponent,
  ...rest
}: SortableTreePropsBase<TreeItemData, TElement>) => {
  const [draggingId, setDraggingId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  const flattenedTree = React.useMemo(() => flattenTree(items), [items]);
  const flattenedItems: FlattenedItem<TreeItemData>[] = useMemo(() => {
    const collapsedIds = without(
      flattenedTree.map((x) => x.id),
      ...expandedIds
    );
    return removeChildrenOf(flattenedTree, draggingId ? [draggingId, ...collapsedIds] : collapsedIds);
  }, [draggingId, expandedIds, flattenedTree]);

  const sensorContext: SensorContext<TreeItemData> = useRef({
    items: flattenedItems,
    offset: offsetLeft
  });
  const [coordinateGetter] = useState(() =>
    sortableTreeKeyboardCoordinates(sensorContext, indicator, indentationWidth, isSameDepthDrag ?? false)
  );
  const pointerSensor = useSensor(PointerSensor, defaultPointerSensorOptions);
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);

  const sortedIds = useMemo(() => flattenedItems.map(({ id }) => id), [flattenedItems]);

  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft
    };
  }, [flattenedItems, offsetLeft]);

  const projected =
    draggingId && overId
      ? getProjection(flattenedItems, draggingId, overId, offsetLeft, indentationWidth, isSameDepthDrag ?? false)
      : null;

  const modifiers = React.useMemo((): Modifier[] | undefined => {
    return undefined; //isSameDepthDrag ? [sameDepthModifier] : undefined;
  }, [isSameDepthDrag]);

  const handleDragStart = React.useCallback(({ active: { id: activeId } }: DragStartEvent) => {
    setDraggingId(activeId);
    setOverId(activeId);

    document.body.style.setProperty("cursor", "grabbing");
  }, []);

  const handleDragMove = React.useCallback(({ delta }: DragMoveEvent) => {
    setOffsetLeft(delta.x);
  }, []);

  const handleDragOver = React.useCallback(({ over }: DragOverEvent) => {
    setOverId(over?.id ?? null);
  }, []);

  const resetState = React.useCallback(() => {
    setOverId(null);
    setDraggingId(null);
    setOffsetLeft(0);

    document.body.style.setProperty("cursor", "");
  }, []);

  const handleDragEnd = React.useCallback(
    ({ active, over }: DragEndEvent) => {
      try {
        if (projected && over) {
          const { depth, parentId } = projected;

          const clonedItems: FlattenedItem<TreeItemData>[] = flattenTree(items);
          const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
          const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
          const activeTreeItem = clonedItems[activeIndex];

          clonedItems[activeIndex] = { ...activeTreeItem, depth, parentId };
          const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
          const newItems = buildTree(sortedItems);
          const newActiveItem = sortedItems.find((x) => x.id === active.id)!;

          const oldIndex = findItemDeep(items, newActiveItem.id)!.index;
          const newIndex = findItemDeep(newItems, newActiveItem.id)!.index;
          if (newIndex !== oldIndex || newActiveItem.parentId !== activeTreeItem.parentId) {
            const parent = clonedItems.find((x) => x.id === parentId);
            const parentType = parent ? parent.data.type : "root";
            if (parentType && newActiveItem.data.parentTypes && !newActiveItem.data.parentTypes.includes(parentType))
              return;

            onDrop!({
              index: newIndex,
              parentId: newActiveItem.parentId ?? null,
              source: newActiveItem,
              items: newItems
            });
          }
        }
      } finally {
        resetState();
      }
    },
    [items, projected, onDrop, resetState]
  );

  const handleDragCancel = React.useCallback(() => {
    resetState();
  }, []);

  return (
    <DndContext
      {...dndContextProps}
      modifiers={modifiers}
      sensors={onDrop ? sensors : undefined}
      collisionDetection={closestCenter}
      measuring={measuring}
      onDragStart={onDrop ? handleDragStart : undefined}
      onDragMove={onDrop ? handleDragMove : undefined}
      onDragOver={onDrop ? handleDragOver : undefined}
      onDragEnd={onDrop ? handleDragEnd : undefined}
      onDragCancel={onDrop ? handleDragCancel : undefined}
    >
      <SortableContext items={sortedIds} strategy={onDrop ? verticalListSortingStrategy : undefined}>
        {flattenedItems.map((item) => (
          <SortableTreeItem
            {...rest}
            key={item.id}
            depth={item.id === draggingId && projected ? projected.depth : item.depth}
            disableAnimation={isNoAnimation}
            id={item.id as any}
            indentationWidth={indentationWidth}
            isActive={active === item.id}
            isDisabled={!onDrop}
            isExpanded={expandedIds.includes(item.id)}
            isHighlighted={item.id === active}
            item={item}
            toggleExpanded={() => toggleExpanded(item.id)}
            TreeItemComponent={TreeItemComponent}
          />
        ))}
        <DragOverlay
          dropAnimation={dropAnimation === undefined ? dropAnimationDefaultConfig : dropAnimation}
          modifiers={indicator ? MODIFIERS : undefined}
        />
      </SortableContext>
    </DndContext>
  );
};

export type SortableTreeMode = "active" | "expanded";
export type SortableTreeProps<TData extends SortableTreeItemData, TElement extends HTMLElement> = Omit<
  SortableTreePropsBase<TData, TElement>,
  "expandedIds" | "toggleExpanded"
> & {
  mode: SortableTreeMode;
};
export const SortableTree = <TreeItemData extends SortableTreeItemData, TElement extends HTMLElement = HTMLDivElement>(
  props: SortableTreeProps<TreeItemData, TElement>
) => {
  const [expandedIds, setExpandedIds] = React.useState<UniqueIdentifier[]>();

  // handles "expanded" mode
  React.useEffect(() => {
    if (props.mode === "expanded") {
      const flatten = flattenTree(props.items);
      setExpandedIds(flatten.filter((x) => x.children?.length).map((x) => x.id));
    }
  }, [props.mode, props.items]);

  // handles "active" mode
  React.useEffect(() => {
    if (props.mode === "active") {
      const flatten = flattenTree(props.items);
      if (props.activeId) {
        const map = toMap(
          flatten,
          (x) => x.id,
          (x) => x
        );
        if (!(props.activeId in map)) return;

        const ids: UniqueIdentifier[] = [];
        let current: FlattenedItem<TreeItemData> | null = map[props.activeId];
        while (current) {
          ids.push(current.id);
          current = current.parentId !== null ? map[current.parentId] : null;
        }

        setExpandedIds((x) => [...new Set<any>([...(x ?? []), ...ids])]);
      }
    }
  }, [props.activeId, props.mode, props.items]);

  const toggleExpanded = React.useCallback(
    (id: UniqueIdentifier) => {
      const eids = expandedIds ?? EMPTY_ARRAY;
      if (eids.includes(id)) {
        setExpandedIds(removeItem(eids, (x) => x === id));
      } else {
        setExpandedIds([...eids, id]);
      }
    },
    [expandedIds]
  );

  return <SortableTreeBase {...props} expandedIds={expandedIds ?? EMPTY_ARRAY} toggleExpanded={toggleExpanded} />;
};
